import { DataRange } from "types";

import { quantile } from "./boxPlotDataProcessing";

const SQRT_2PI = Math.sqrt(2 * Math.PI);
const IQR_SCALE = 1.35;
const PERCENTILE_COUNT = 101;
// The Silverman n-term is the fixed percentile-array length (101^(-1/5)), not
// the sample count — the KDE runs over P0…P100, never the raw values.
const N_FACTOR = Math.pow(PERCENTILE_COUNT, -1 / 5);

const TRIM_BOUNDS: Record<DataRange, [number, number] | null> = {
  all: null,
  p1p99: [0.01, 0.99],
  p5p95: [0.05, 0.95]
};

export const DATA_RANGE_LABELS: Record<DataRange, string> = {
  all: "All data",
  p1p99: "P1–P99",
  p5p95: "P5–P95"
};

export interface ViolinCurvePoint {
  value: number;
  density: number;
}

export interface ViolinStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  mean: number;
  p10: number;
  p90: number;
}

export interface ViolinGroup {
  name?: string;
  n: number;
  curve: ViolinCurvePoint[];
  groupMaxDensity: number;
  stats: ViolinStats;
  degenerate: boolean;
}

export function gaussianKernel(u: number): number {
  return Math.exp(-0.5 * u * u) / SQRT_2PI;
}

/**
 * The percentile bounds a Data Range keeps, computed from the given finite values.
 * The Insights engine computes these bounds ONCE over the whole chart's values (all
 * groups pooled) and filters every group against them — pass the pooled values here
 * to match. Returns null for "all" (nothing to trim) and for input with no finite
 * values (no bounds exist).
 *
 * This is shared by every box-plot display mode (box/jitter/violin, via boxPlot.ts),
 * so it stays on type-7 to leave box/jitter Data-Range trimming unchanged. Only the
 * violin's silhouette, box quartiles and bandwidth use R-5 (approxPercentile); over a
 * pooled well population the two conventions' p1/p99 differ by a sub-well amount.
 */
export function trimBounds(
  values: number[],
  range: DataRange
): { lo: number; hi: number } | null {
  const bounds = TRIM_BOUNDS[range];
  if (!bounds) return null;

  const clean = stripValues(values);
  if (clean.length === 0) return null;

  const asc = [...clean].sort((a, b) => a - b);
  return { lo: quantile(asc, bounds[0]), hi: quantile(asc, bounds[1]) };
}

/** Finite values within the inclusive bounds; non-finite values are always dropped. */
export function filterValuesWithinBounds(
  values: number[],
  bounds: { lo: number; hi: number }
): number[] {
  return stripValues(values).filter((v) => v >= bounds.lo && v <= bounds.hi);
}

function stripValues(values: number[]): number[] {
  return (values ?? []).filter((v) => typeof v === "number" && Number.isFinite(v));
}

export function populationStandardDeviation(values: number[], mean: number): number {
  const n = values.length;
  if (n === 0) return 0;
  const variance = values.reduce((acc, v) => acc + (v - mean) * (v - mean), 0) / n;
  return Math.sqrt(variance);
}

/**
 * Kinetica's APPROX_PERCENTILE — the source of the Insights violin's box quartiles
 * and its 101-point silhouette — is the R-5 / Hazen quantile: position
 * h = n·p + 0.5, clamped to [1, n], with linear interpolation between the
 * bracketing order statistics. Reverse-engineered from Insights
 * `groupedDistribution` output, which it reproduces value-for-value on the shared
 * well set (e.g. an n=4 group has Q1 = mean of the 1st–2nd order statistics,
 * Q3 = mean of the 3rd–4th). This is NOT type-7 (`h = (n-1)p + 1`), whose interior
 * interpolation and un-clamped tails diverge from Insights on small groups.
 * `asc` must be sorted ascending.
 *
 * Note: the pooled Data-Range trim bounds are the one percentile that does NOT use
 * this — `trimBounds` stays on type-7 because it is shared with box/jitter (see its
 * own comment). So with a trim active the trimmed input set can differ from Insights,
 * and "value-for-value" holds unconditionally only for the untrimmed ("all") range.
 */
export function approxPercentile(asc: number[], p: number): number {
  const n = asc.length;
  if (n === 0) return NaN;
  if (n === 1) return asc[0];
  const h = Math.min(Math.max(n * p + 0.5, 1), n);
  const lo = Math.floor(h);
  if (lo >= n) return asc[n - 1];
  return asc[lo - 1] + (h - lo) * (asc[lo] - asc[lo - 1]);
}

export function buildPercentiles(asc: number[]): number[] {
  const percentiles: number[] = [];
  for (let i = 0; i < PERCENTILE_COUNT; i++) {
    percentiles.push(approxPercentile(asc, i / (PERCENTILE_COUNT - 1)));
  }
  return percentiles;
}

// Core bandwidth computation over an already-cleaned, ascending-sorted array.
// Kept private so callers that already hold a sorted array (buildViolinGroup) can
// avoid re-sorting; the zero-IQR fallback (h = 0.9·σ_pop) matches Insights.
function bandwidthFromSorted(asc: number[], bwAdjust: number): number {
  const n = asc.length;
  if (n === 0) return 0;

  const mean = asc.reduce((a, b) => a + b, 0) / n;
  const sd = populationStandardDeviation(asc, mean);
  const iqr = approxPercentile(asc, 0.75) - approxPercentile(asc, 0.25);

  const spread = Math.min(sd, iqr / IQR_SCALE);
  let h = 0.9 * spread * N_FACTOR;
  if (h === 0) h = 0.9 * sd;

  return h * bwAdjust;
}

export function kde(values: number[], h: number, grid: number[]): number[] {
  const n = values.length;
  if (n === 0 || h <= 0) return grid.map(() => 0);
  return grid.map((x) => {
    let sum = 0;
    for (let i = 0; i < n; i++) {
      sum += gaussianKernel((x - values[i]) / h);
    }
    return sum / (n * h);
  });
}

function computeStats(values: number[]): ViolinStats {
  const asc = [...values].sort((a, b) => a - b);
  const n = asc.length;
  if (n === 0) {
    return { min: 0, q1: 0, median: 0, q3: 0, max: 0, mean: 0, p10: 0, p90: 0 };
  }
  // Mirror the Insights groupedDistribution box: each quartile is an index into
  // the same APPROX_PERCENTILE (R-5) skeleton the silhouette runs on — min = P0,
  // Q1 = P25, median = P50, Q3 = P75, max = P100, P10/P90 for the whiskers — with
  // the raw arithmetic mean (Insights reads it from a separate SQL avg()).
  const mean = asc.reduce((a, b) => a + b, 0) / n;
  return {
    min: asc[0],
    q1: approxPercentile(asc, 0.25),
    median: approxPercentile(asc, 0.5),
    q3: approxPercentile(asc, 0.75),
    max: asc[n - 1],
    mean,
    p10: approxPercentile(asc, 0.1),
    p90: approxPercentile(asc, 0.9)
  };
}

export function buildViolinGroup(
  rawValues: number[],
  opts: { name?: string; bwAdjust?: number } = {}
): ViolinGroup {
  const values = stripValues(rawValues);
  const n = values.length;
  const stats = computeStats(values);

  if (n === 0) {
    return {
      name: opts.name,
      n: 0,
      curve: [],
      groupMaxDensity: 0,
      stats,
      degenerate: true
    };
  }

  // Sort once and thread the ascending array through both the bandwidth and the
  // percentile steps (previously each re-sorted independently).
  const asc = [...values].sort((a, b) => a - b);
  const h = bandwidthFromSorted(asc, opts.bwAdjust ?? 1);

  if (!(h > 0)) {
    return {
      name: opts.name,
      n,
      curve: [{ value: stats.median, density: 1 }],
      groupMaxDensity: 1,
      stats,
      degenerate: true
    };
  }

  // The 101 percentiles P0…P100 are both the KDE sample set and the evaluation
  // grid, matching the Insights implementation. Non-uniform value spacing.
  const percentiles = buildPercentiles(asc);
  const densities = kde(percentiles, h, percentiles);
  const curve = percentiles.map((value, i) => ({ value, density: densities[i] }));
  const groupMaxDensity = Math.max(...densities);

  return {
    name: opts.name,
    n,
    curve,
    groupMaxDensity,
    stats,
    degenerate: false
  };
}
